import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { productosAPI, uploadAPI, getImageUrl, productoMediaAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import ImageLightbox from '../components/ImageLightbox';
import ProductMediaManager from '../components/ProductMediaManager';

const TIPOS_PRODUCTO = [
  { value: '', label: 'Seleccionar...' },
  { value: 'armazon', label: 'Armazón' },
  { value: 'lente_oftalmico', label: 'Lente Oftálmico' },
  { value: 'lente_sol', label: 'Lente de Sol' },
  { value: 'lente_contacto', label: 'Lente de Contacto' },
  { value: 'solucion', label: 'Solución/Líquido' },
  { value: 'accesorio', label: 'Accesorio' },
  { value: 'equipo', label: 'Equipo Óptico' },
  { value: 'medicamento', label: 'Gotas/Medicamento' },
  { value: 'otro', label: 'Otro' }
];

const GENEROS = [
  { value: 'unisex', label: 'Unisex' },
  { value: 'hombre', label: 'Hombre' },
  { value: 'mujer', label: 'Mujer' },
  { value: 'nino', label: 'Niño' },
  { value: 'nina', label: 'Niña' }
];

const TIPOS_LENTE = [
  { value: '', label: 'Seleccionar...' },
  { value: 'monofocal', label: 'Monofocal' },
  { value: 'bifocal', label: 'Bifocal' },
  { value: 'progresivo', label: 'Progresivo' },
  { value: 'transitions', label: 'Transitions/Fotocromático' },
  { value: 'blue_block', label: 'Blue Block' },
  { value: 'polarizado', label: 'Polarizado' }
];

const INDICES_REFRACCION = [
  { value: '', label: 'Seleccionar...' },
  { value: '1.50', label: '1.50 (Estándar)' },
  { value: '1.56', label: '1.56 (Medio)' },
  { value: '1.61', label: '1.61 (Alto)' },
  { value: '1.67', label: '1.67 (Muy Alto)' },
  { value: '1.74', label: '1.74 (Ultra Alto)' }
];

const TIPOS_CONTACTO = [
  { value: '', label: 'Seleccionar...' },
  { value: 'blando', label: 'Blando' },
  { value: 'rigido', label: 'Rígido (RGP)' },
  { value: 'hibrido', label: 'Híbrido' }
];

const USOS_CONTACTO = [
  { value: '', label: 'Seleccionar...' },
  { value: 'diario', label: 'Diario (1 día)' },
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'anual', label: 'Anual' }
];

const Productos = () => {
  const { categorias, marcas, proveedores, formatCurrency, config } = useApp();
  const { modoEspectador } = useAuth();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('basico');
  const [filtros, setFiltros] = useState({ buscar: '', categoria: '', marca: '', tipo_producto: '', genero: '' });
  const [lightboxImage, setLightboxImage] = useState(null); // legacy single image
  const [lightboxGallery, setLightboxGallery] = useState(null); // { items, initialIndex }
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [codigoStatus, setCodigoStatus] = useState({ checking: false, exists: false, message: '' });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const codigoTimeoutRef = useRef(null);
  
  const initialFormData = {
    codigo: '', nombre: '', descripcion: '', categoria_id: '', marca_id: '', proveedor_id: '',
    precio_compra: '', precio_venta: '', iva_porcentaje: 15, stock_actual: 0, stock_minimo: 5, stock_maximo: 100,
    // Campos óptica
    modelo: '', color: '', material: '',
    medida_puente: '', medida_varilla: '', medida_horizontal: '', medida_vertical: '',
    genero: 'unisex', tipo_producto: '', tipo_lente: '', indice_refraccion: '', tratamiento: '',
    tipo_contacto: '', uso_contacto: '', radio_curvatura: '', diametro: '',
    poder_esfera: '', cilindro: '', eje: '', adicion: '',
    imagen_url: '', atributos_extra: {}
  };
  
  const [formData, setFormData] = useState(initialFormData);

  const loadProductos = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 20, page };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await productosAPI.getAll(params);
      if (res.data.success) {
        setProductos(res.data.data);
        setTotal(res.data.total || res.data.data.length);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (e) { toast.error('Error al cargar productos'); }
    finally { setLoading(false); }
  }, [filtros, page]);

  useEffect(() => { loadProductos(); }, [loadProductos]);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (codigoTimeoutRef.current) {
        clearTimeout(codigoTimeoutRef.current);
      }
    };
  }, []);

  // Reset page when filters change
  const handleFiltroChangeWithReset = (e) => {
    setPage(1);
    handleFiltroChange(e);
  };

  // Verificar código en tiempo real
  const verificarCodigo = async (codigo) => {
    if (!codigo || codigo.trim() === '') {
      setCodigoStatus({ checking: false, exists: false, message: '' });
      return;
    }
    
    setCodigoStatus({ checking: true, exists: false, message: 'Verificando...' });
    
    try {
      const res = await productosAPI.verificarCodigo(codigo, editando?.id);
      if (res.data.existe) {
        setCodigoStatus({ checking: false, exists: true, message: '⚠️ Este código ya existe' });
      } else {
        setCodigoStatus({ checking: false, exists: false, message: '✓ Código disponible' });
      }
    } catch (e) {
      setCodigoStatus({ checking: false, exists: false, message: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Verificar código con debounce
    if (name === 'codigo') {
      if (codigoTimeoutRef.current) clearTimeout(codigoTimeoutRef.current);
      codigoTimeoutRef.current = setTimeout(() => verificarCodigo(value), 500);
    }
  };
  
  const handleFiltroChange = (e) => setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB'); return; }
    
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      if (res.data.success) {
        setFormData(prev => ({ ...prev, imagen_url: res.data.data.url }));
        toast.success('Imagen subida');
      }
    } catch (e) { toast.error('Error al subir imagen'); }
    finally { setUploading(false); }
  };

  const openModal = (producto = null) => {
    if (producto) {
      setEditando(producto);
      setFormData({
        ...initialFormData,
        ...producto,
        categoria_id: producto.categoria_id || '',
        marca_id: producto.marca_id || '',
        proveedor_id: producto.proveedor_id || ''
      });
    } else {
      setEditando(null);
      setFormData({ ...initialFormData, iva_porcentaje: config.iva_defecto || 15 });
    }
    setCodigoStatus({ checking: false, exists: false, message: '' });
    setActiveTab('basico');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditando(null);
    setCodigoStatus({ checking: false, exists: false, message: '' });
  };

  // Auto-guardar producto al ir a tab de media (si aún no existe)
  const handleMediaTab = async () => {
    if (editando) {
      setActiveTab('media');
      return;
    }
    // Validar campos mínimos
    if (!formData.nombre?.trim()) {
      toast.warning('Ingresa al menos el nombre del producto antes de subir fotos');
      setActiveTab('basico');
      return;
    }
    if (!formData.precio_venta || parseFloat(formData.precio_venta) <= 0) {
      toast.warning('Ingresa el precio de venta antes de subir fotos');
      setActiveTab('basico');
      return;
    }
    // Auto-guardar el producto
    try {
      const data = {
        ...formData,
        categoria_id: formData.categoria_id || null,
        marca_id: formData.marca_id || null,
        proveedor_id: formData.proveedor_id || null,
        precio_compra: parseFloat(formData.precio_compra) || 0,
        precio_venta: parseFloat(formData.precio_venta) || 0,
        iva_porcentaje: parseFloat(formData.iva_porcentaje) || 15,
        stock_actual: parseInt(formData.stock_actual) || 0,
        stock_minimo: parseInt(formData.stock_minimo) || 5,
        stock_maximo: parseInt(formData.stock_maximo) || 100
      };
      const createRes = await productosAPI.create(data);
      const nuevoProducto = createRes.data.data;
      setEditando(nuevoProducto);
      setFormData(prev => ({ ...prev, ...nuevoProducto }));
      setActiveTab('media');
      toast.success('Producto guardado. Ahora agrega fotos y videos.');
      loadProductos();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar producto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    if (codigoStatus.exists) { toast.error('El código ya existe, elige otro'); return; }
    
    try {
      const data = {
        ...formData,
        categoria_id: formData.categoria_id || null, 
        marca_id: formData.marca_id || null, 
        proveedor_id: formData.proveedor_id || null,
        precio_compra: parseFloat(formData.precio_compra) || 0, 
        precio_venta: parseFloat(formData.precio_venta) || 0,
        iva_porcentaje: parseFloat(formData.iva_porcentaje) || 15, 
        stock_actual: parseInt(formData.stock_actual) || 0,
        stock_minimo: parseInt(formData.stock_minimo) || 5, 
        stock_maximo: parseInt(formData.stock_maximo) || 100
      };
      
      if (editando) {
        await productosAPI.update(editando.id, data);
        toast.success('Producto actualizado');
        closeModal();
      } else {
        const createRes = await productosAPI.create(data);
        toast.success('Producto creado. Ahora puedes agregar fotos y videos.');
        // Abrir tab de media con el producto recien creado
        const nuevoProducto = createRes.data.data;
        setEditando(nuevoProducto);
        setFormData(prev => ({ ...prev, ...nuevoProducto }));
        setActiveTab('media');
      }
      loadProductos();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try { 
      await productosAPI.delete(id); 
      toast.success('Producto eliminado'); 
      loadProductos(); 
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const getEstadoStock = (p) => {
    if (p.stock_actual <= 0) return { clase: 'sin-stock', texto: 'Sin Stock' };
    if (p.stock_actual <= p.stock_minimo) return { clase: 'bajo', texto: 'Stock Bajo' };
    if (p.stock_actual >= p.stock_maximo) return { clase: 'alto', texto: 'Stock Alto' };
    return { clase: 'normal', texto: 'Normal' };
  };

  const getGeneroLabel = (g) => GENEROS.find(x => x.value === g)?.label || g;
  const getTipoLabel = (t) => TIPOS_PRODUCTO.find(x => x.value === t)?.label || t;

  const hayFiltros = Object.values(filtros).some(v => v);

  return (
    <div>
      {/* Filtros */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Buscar</label>
              <input type="text" className="form-control" placeholder="Código, nombre, modelo..." name="buscar" value={filtros.buscar} onChange={handleFiltroChangeWithReset} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Categoría</label>
              <select className="form-select" name="categoria" value={filtros.categoria} onChange={handleFiltroChangeWithReset}>
                <option value="">Todas</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Tipo</label>
              <select className="form-select" name="tipo_producto" value={filtros.tipo_producto} onChange={handleFiltroChangeWithReset}>
                {TIPOS_PRODUCTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Género</label>
              <select className="form-select" name="genero" value={filtros.genero} onChange={handleFiltroChangeWithReset}>
                <option value="">Todos</option>
                {GENEROS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-2">
                {hayFiltros && (
                  <button className="btn btn-outline-secondary" onClick={() => { setFiltros({ buscar: '', categoria: '', marca: '', tipo_producto: '', genero: '' }); setPage(1); }}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
                <button className="btn btn-primary flex-grow-1" onClick={() => openModal()} disabled={modoEspectador} title={modoEspectador ? 'No disponible en modo espectador' : ''}>
                  <i className="bi bi-plus-lg me-2"></i>Nuevo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? <div className="loading-container"><div className="spinner-border text-primary"></div></div>
          : productos.length === 0 ? (
            <div className="empty-state py-5">
              <i className="bi bi-box-seam"></i>
              <h5>No hay productos</h5>
              <button className="btn btn-primary mt-3" onClick={() => openModal()} disabled={modoEspectador}>
                <i className="bi bi-plus-lg me-2"></i>Agregar Producto
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{width:'50px'}}></th>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Género</th>
                    <th className="text-end">P. Venta</th>
                    <th className="text-center">Stock</th>
                    <th className="text-center">Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map(p => {
                    const estado = getEstadoStock(p);
                    const imgUrl = getImageUrl(p.imagen_url);
                    const mediaCounts = p.media_counts || {};
                    const totalMedia = (mediaCounts.imagenes_count || 0) + (mediaCounts.videos_count || 0);
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="position-relative d-inline-block">
                            {imgUrl ? (
                              <img src={imgUrl} alt={p.nombre} className="product-image" onClick={async () => {
                                // Abrir galeria si tiene multiples medias
                                if (totalMedia > 1) {
                                  try {
                                    const res = await productoMediaAPI.getMedia(p.id);
                                    if (res.data.success) {
                                      const items = [
                                        ...(res.data.data.imagenes || []).map(m => ({ type: 'image', url: getImageUrl(m.url) })),
                                        ...(res.data.data.videos || []).map(m => ({ type: 'video', url: getImageUrl(m.url) }))
                                      ];
                                      if (items.length > 0) {
                                        setLightboxGallery({ items, initialIndex: 0 });
                                        return;
                                      }
                                    }
                                  } catch(e) {}
                                }
                                setLightboxImage(imgUrl);
                              }} />
                            ) : (
                              <div className="product-image-placeholder"><i className="bi bi-image"></i></div>
                            )}
                            {totalMedia > 1 && (
                              <span className="position-absolute top-0 end-0 badge bg-primary" style={{ fontSize: '0.6rem', transform: 'translate(30%, -30%)' }}>
                                {mediaCounts.imagenes_count || 0}<i className="bi bi-image ms-1" style={{ fontSize: '0.5rem' }}></i>
                                {mediaCounts.videos_count > 0 && <> +{mediaCounts.videos_count}<i className="bi bi-camera-video ms-1" style={{ fontSize: '0.5rem' }}></i></>}
                              </span>
                            )}
                          </div>
                        </td>
                        <td><code>{p.codigo}</code></td>
                        <td>
                          <div className="fw-medium">{p.nombre}</div>
                          {p.modelo && <small className="text-muted">Modelo: {p.modelo}</small>}
                          {p.color && <small className="text-muted ms-2">| {p.color}</small>}
                        </td>
                        <td><small>{getTipoLabel(p.tipo_producto)}</small></td>
                        <td><span className={`badge bg-${p.genero === 'hombre' ? 'primary' : p.genero === 'mujer' ? 'danger' : 'secondary'}`}>{getGeneroLabel(p.genero)}</span></td>
                        <td className="text-end">{formatCurrency(p.precio_venta)}</td>
                        <td className="text-center"><strong>{p.stock_actual}</strong></td>
                        <td className="text-center"><span className={`badge-stock ${estado.clase}`}>{estado.texto}</span></td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={() => openModal(p)} title="Editar" disabled={modoEspectador}><i className="bi bi-pencil"></i></button>
                            <button className="btn btn-outline-danger" onClick={() => handleDelete(p.id)} title="Eliminar" disabled={modoEspectador}><i className="bi bi-trash"></i></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {productos.length > 0 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <span className="text-muted">Mostrando {productos.length} de {total} producto(s)</span>
            {totalPages > 1 && (
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pg;
                    if (totalPages <= 5) pg = i + 1;
                    else if (page <= 3) pg = i + 1;
                    else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                    else pg = page - 2 + i;
                    return (
                      <li key={pg} className={`page-item ${page === pg ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setPage(pg)}>{pg}</button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${editando ? 'bi-pencil' : 'bi-plus-circle'} me-2`}></i>
                  {editando ? 'Editar Producto' : 'Nuevo Producto'}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* User tracking info */}
                  {editando && (editando.creado_por_nombre || editando.modificado_por_nombre) && (
                    <div className="mb-3 d-flex flex-wrap align-items-center">
                      {editando.creado_por_nombre && (
                        <small className="text-muted">
                          <i className="bi bi-person-plus me-1"></i>Creado por: {editando.creado_por_nombre}
                        </small>
                      )}
                      {editando.modificado_por_nombre && (
                        <small className="text-muted ms-3">
                          <i className="bi bi-pencil me-1"></i>Modificado por: {editando.modificado_por_nombre}
                        </small>
                      )}
                    </div>
                  )}
                  {/* Tabs */}
                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'basico' ? 'active' : ''}`} onClick={() => setActiveTab('basico')}>
                        <i className="bi bi-info-circle me-1"></i>Básico
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'optica' ? 'active' : ''}`} onClick={() => setActiveTab('optica')}>
                        <i className="bi bi-eyeglasses me-1"></i>Óptica
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'contacto' ? 'active' : ''}`} onClick={() => setActiveTab('contacto')}>
                        <i className="bi bi-circle me-1"></i>Lentes Contacto
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'media' ? 'active' : ''}`} onClick={handleMediaTab}>
                        <i className="bi bi-images me-1"></i>Fotos y Videos
                      </button>
                    </li>
                  </ul>

                  {/* Tab: Básico */}
                  {activeTab === 'basico' && (
                    <div className="row g-3 fade-in">
                      <div className="col-md-3">
                        <label className="form-label">Código</label>
                        <input 
                          type="text" 
                          className={`form-control ${codigoStatus.exists ? 'is-invalid' : codigoStatus.message && !codigoStatus.checking ? 'is-valid' : ''}`}
                          name="codigo" 
                          value={formData.codigo} 
                          onChange={handleChange} 
                          placeholder="Automático si vacío" 
                        />
                        {codigoStatus.message && (
                          <small className={codigoStatus.exists ? 'text-danger' : 'text-success'}>
                            {codigoStatus.checking && <span className="spinner-border spinner-border-sm me-1"></span>}
                            {codigoStatus.message}
                          </small>
                        )}
                        {!formData.codigo && !codigoStatus.message && (
                          <small className="text-muted">Se generará automáticamente</small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Nombre *</label>
                        <input type="text" className="form-control" name="nombre" value={formData.nombre} onChange={handleChange} required />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Modelo</label>
                        <input type="text" className="form-control" name="modelo" value={formData.modelo} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Descripción</label>
                        <textarea className="form-control" name="descripcion" value={formData.descripcion} onChange={handleChange} rows="2" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Categoría</label>
                        <select className="form-select" name="categoria_id" value={formData.categoria_id} onChange={handleChange}>
                          <option value="">Seleccionar...</option>
                          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Marca</label>
                        <select className="form-select" name="marca_id" value={formData.marca_id} onChange={handleChange}>
                          <option value="">Seleccionar...</option>
                          {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Proveedor</label>
                        <select className="form-select" name="proveedor_id" value={formData.proveedor_id} onChange={handleChange}>
                          <option value="">Seleccionar...</option>
                          {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Tipo de Producto</label>
                        <select className="form-select" name="tipo_producto" value={formData.tipo_producto} onChange={handleChange}>
                          {TIPOS_PRODUCTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Género</label>
                        <select className="form-select" name="genero" value={formData.genero} onChange={handleChange}>
                          {GENEROS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Color</label>
                        <input type="text" className="form-control" name="color" value={formData.color} onChange={handleChange} />
                      </div>
                      
                      <div className="col-12"><hr className="my-2" /><h6 className="text-muted">Precios y Stock</h6></div>
                      
                      <div className="col-md-3">
                        <label className="form-label">Precio Compra</label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input type="number" step="0.01" className="form-control" name="precio_compra" value={formData.precio_compra} onChange={handleChange} />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Precio Venta</label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input type="number" step="0.01" className="form-control" name="precio_venta" value={formData.precio_venta} onChange={handleChange} />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">IVA %</label>
                        <input type="number" step="0.01" className="form-control" name="iva_porcentaje" value={formData.iva_porcentaje} onChange={handleChange} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Precio + IVA</label>
                        <input type="text" className="form-control bg-light" value={formatCurrency(parseFloat(formData.precio_venta || 0) * (1 + parseFloat(formData.iva_porcentaje || 0) / 100))} readOnly />
                      </div>
                      {!editando && (
                        <div className="col-md-4">
                          <label className="form-label">Stock Inicial</label>
                          <input type="number" className="form-control" name="stock_actual" value={formData.stock_actual} onChange={handleChange} />
                        </div>
                      )}
                      <div className={editando ? 'col-md-6' : 'col-md-4'}>
                        <label className="form-label">Stock Mínimo</label>
                        <input type="number" className="form-control" name="stock_minimo" value={formData.stock_minimo} onChange={handleChange} />
                      </div>
                      <div className={editando ? 'col-md-6' : 'col-md-4'}>
                        <label className="form-label">Stock Máximo</label>
                        <input type="number" className="form-control" name="stock_maximo" value={formData.stock_maximo} onChange={handleChange} />
                      </div>
                    </div>
                  )}

                  {/* Tab: Óptica */}
                  {activeTab === 'optica' && (
                    <div className="row g-3 fade-in">
                      <div className="col-12">
                        <div className="alert alert-info py-2">
                          <i className="bi bi-info-circle me-2"></i>
                          Campos específicos para armazones y lentes oftálmicos
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Material</label>
                        <input type="text" className="form-control" name="material" value={formData.material} onChange={handleChange} placeholder="Ej: Acetato, Metal, Titanio" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Tipo de Lente</label>
                        <select className="form-select" name="tipo_lente" value={formData.tipo_lente} onChange={handleChange}>
                          {TIPOS_LENTE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Índice de Refracción</label>
                        <select className="form-select" name="indice_refraccion" value={formData.indice_refraccion} onChange={handleChange}>
                          {INDICES_REFRACCION.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Tratamiento</label>
                        <input type="text" className="form-control" name="tratamiento" value={formData.tratamiento} onChange={handleChange} placeholder="Ej: Antirreflejo, Blue Block" />
                      </div>
                      
                      <div className="col-12"><hr className="my-2" /><h6 className="text-muted">Medidas del Armazón</h6></div>
                      
                      <div className="col-md-3">
                        <label className="form-label">Puente (mm)</label>
                        <input type="text" className="form-control" name="medida_puente" value={formData.medida_puente} onChange={handleChange} placeholder="Ej: 18" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Varilla (mm)</label>
                        <input type="text" className="form-control" name="medida_varilla" value={formData.medida_varilla} onChange={handleChange} placeholder="Ej: 140" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Ancho Lente (mm)</label>
                        <input type="text" className="form-control" name="medida_horizontal" value={formData.medida_horizontal} onChange={handleChange} placeholder="Ej: 52" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Alto Lente (mm)</label>
                        <input type="text" className="form-control" name="medida_vertical" value={formData.medida_vertical} onChange={handleChange} placeholder="Ej: 40" />
                      </div>
                    </div>
                  )}

                  {/* Tab: Lentes de Contacto */}
                  {activeTab === 'contacto' && (
                    <div className="row g-3 fade-in">
                      <div className="col-12">
                        <div className="alert alert-info py-2">
                          <i className="bi bi-info-circle me-2"></i>
                          Campos específicos para lentes de contacto
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Tipo de Contacto</label>
                        <select className="form-select" name="tipo_contacto" value={formData.tipo_contacto} onChange={handleChange}>
                          {TIPOS_CONTACTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Uso/Reemplazo</label>
                        <select className="form-select" name="uso_contacto" value={formData.uso_contacto} onChange={handleChange}>
                          {USOS_CONTACTO.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Radio Curvatura (BC)</label>
                        <input type="text" className="form-control" name="radio_curvatura" value={formData.radio_curvatura} onChange={handleChange} placeholder="Ej: 8.6" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Diámetro (DIA)</label>
                        <input type="text" className="form-control" name="diametro" value={formData.diametro} onChange={handleChange} placeholder="Ej: 14.2" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Esfera (PWR/SPH)</label>
                        <input type="text" className="form-control" name="poder_esfera" value={formData.poder_esfera} onChange={handleChange} placeholder="Ej: -2.50" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Cilindro (CYL)</label>
                        <input type="text" className="form-control" name="cilindro" value={formData.cilindro} onChange={handleChange} placeholder="Ej: -0.75" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Eje (AXIS)</label>
                        <input type="text" className="form-control" name="eje" value={formData.eje} onChange={handleChange} placeholder="Ej: 180" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Adición (ADD)</label>
                        <input type="text" className="form-control" name="adicion" value={formData.adicion} onChange={handleChange} placeholder="Ej: +2.00" />
                      </div>
                    </div>
                  )}

                  {/* Tab: Fotos y Videos (solo se muestra si el producto ya fue guardado) */}
                  {activeTab === 'media' && editando && (
                    <div className="fade-in">
                      <ProductMediaManager
                        productoId={editando.id}
                        onMediaChange={loadProductos}
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-check-lg me-2"></i>{editando ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {lightboxImage && <ImageLightbox src={lightboxImage} alt="Producto" onClose={() => setLightboxImage(null)} />}
      {lightboxGallery && <ImageLightbox items={lightboxGallery.items} initialIndex={lightboxGallery.initialIndex} onClose={() => setLightboxGallery(null)} />}
    </div>
  );
};

export default Productos;
